import { useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';

import type { ChatMessage } from '@karehero/models';

import { useDispatch } from 'store';
import {
  chatSessionApi,
  useAppendChatMessageMutation,
  useUpdateChatMessageMutation,
} from 'store/api/chatSession';
import type { UpdateMessage } from './useMessageOutbox';
import { toApiMessage } from 'utils/chatMessages';

/** The listChatSessions query whose cached session is written to. */
export interface ChatSessionQueryArgs {
  careCircleId: string;
  mode: string;
  status?: string;
}

export interface UseChatSessionWriterOptions {
  careCircleId?: string;
  sessionId?: string | null;
  queryArgs: ChatSessionQueryArgs;
}

/**
 * useChatSessionWriter writes a chat session's messages: each lands in the
 * cached session at once, and goes to the server in the order it was said.
 */
export const useChatSessionWriter = ({
  careCircleId,
  sessionId,
  queryArgs,
}: UseChatSessionWriterOptions) => {
  const dispatch = useDispatch();
  const [appendChatMessage] = useAppendChatMessageMutation();
  const [updateChatMessageApi] = useUpdateChatMessageMutation();

  // Session writes go one at a time, so the server keeps messages in the order
  // they were said, and the refetch after saving progress has them all.
  const writeQueue = useRef<Promise<unknown>>(Promise.resolve());
  const enqueueWrite = useCallback((write: () => Promise<unknown>) => {
    writeQueue.current = writeQueue.current
      .then(write)
      .catch((err) => Sentry.captureException(err));
  }, []);

  const persistMessage = useCallback(
    (msg: ChatMessage) => {
      if (!sessionId || !careCircleId) return;

      const apiMsg = toApiMessage(msg);

      // Optimistic update: patch the query cache immediately.
      dispatch(
        chatSessionApi.util.updateQueryData(
          'listChatSessions',
          queryArgs,
          (draft) => {
            const session = draft.find((s) => s.session.id === sessionId);
            if (session) session.messages.push(apiMsg);
          },
        ),
      );

      // A rejection is reported, not retried; the message stays shown.
      enqueueWrite(() =>
        appendChatMessage({
          careCircleId,
          sessionId,
          body: {
            id: msg.id,
            senderKind: msg.from,
            contentKind: msg.content.kind,
            content: msg.content,
            status: msg.status,
          },
        }).unwrap(),
      );
    },
    [
      appendChatMessage,
      careCircleId,
      dispatch,
      enqueueWrite,
      queryArgs,
      sessionId,
    ],
  );

  // Sent and failed are saved with the session, so a reload shows them.
  const updateMessage = useCallback<UpdateMessage>(
    (messageId, { status, content }) => {
      const documentId =
        content && 'documentId' in content ? content.documentId : undefined;
      dispatch(
        chatSessionApi.util.updateQueryData(
          'listChatSessions',
          queryArgs,
          (draft) => {
            const session = draft.find((s) => s.session.id === sessionId);
            const msg = session?.messages.find((m) => m.id === messageId);
            if (!msg) return;
            msg.status = status;
            if (content) Object.assign(msg.content, content);
            if (documentId) msg.documentId = documentId;
          },
        ),
      );
      if (status === 'sending' || !sessionId || !careCircleId) return;
      updateChatMessageApi({
        careCircleId,
        sessionId,
        messageId,
        body: { status, documentId },
      })
        .unwrap()
        .catch((err) => Sentry.captureException(err));
    },
    [careCircleId, dispatch, queryArgs, sessionId, updateChatMessageApi],
  );

  return {
    enqueueWrite,
    persistMessage,
    updateMessage,
  };
};
